<!--
  =====================================================================
  PRODUCT CATEGORY CARD - CATEGORY WITH IMAGE
  =====================================================================
  
  Product category card with optional image display.
  
  FEATURES:
  - Image-on-left, text-on-right layout
  - Color scheme support
  - Click navigation to category details
  - Animated vertical bar on hover
  - Responsive resizing
  
  BEHAVIOR:
  - Image fixed width for layout consistency
  - Text expands to fill space when no image
  - Text remains readable at all sizes
  
  =====================================================================
-->

<template>
  <article :class="['category-card', `category-card--${colorScheme}`]">
    <a :href="link" @click.prevent="navigateTo(link)">
      
      <div class="vertical-bar"></div>

      <div class="card-inner">
        
        <div v-if="firstImage" class="card-image">
          <div class="image-container">
            <img :src="firstImage" :alt="card.label" />
          </div>
        </div>

        <div class="card-content">
          <h3 class="card-title">{{ card.title }}</h3>
          <p class="card-text">{{ card.eyebrow }}</p>
        </div>
      </div>
    </a>
  </article>
</template>

<script setup>
import { computed } from 'vue'

// ───────────────────────────────────────────────────────────────────
// PROPS DEFINITION
// ───────────────────────────────────────────────────────────────────

/**
 * Component props definition
 * @prop {Object} card - Card data with title, eyebrow, image, slug
 * @prop {String} colorScheme - Color theme (default: 'green')
 * @prop {String} macroArea - Product macro area slug
 * @prop {String} category - Product category slug
 */
const props = defineProps({
  card: {
    type: Object,
    required: true
  },
  colorScheme: {
    type: String,
    default: 'green'
  },
  macroArea: {
    type: String,
    required: true
  },
  category: {
    type: String,
    required: true
  }
})

// ───────────────────────────────────────────────────────────────────
// COMPUTED PROPERTIES
// ───────────────────────────────────────────────────────────────────

/**
 * Generates card detail link
 * Format: /cassa/prodotti/{macroArea}/{category}/{slug}
 */
const link = computed(() => `/cassa/prodotti/${props.macroArea}/${props.category}/${props.card.slug}`)

/**
 * Extracts first image from card
 * Handles both string and array formats
 */
const firstImage = computed(() => {
  const img = props.card?.image
  if (Array.isArray(img)) {
    return img.length > 0 ? img[0] : null
  }
  return img || null
})

// ───────────────────────────────────────────────────────────────────
// NAVIGATION HANDLER
// ───────────────────────────────────────────────────────────────────

/**
 * Navigates to category details page
 * @param {String} url - Category URL
 */
function navigateTo(url) {
  window.location.href = url
}
</script>

<style scoped lang="scss">
@import "@/assets/styles/partials/variables";
@import "@/assets/styles/partials/compat";

/* ------------------------------------------------------------------
   Stili card categoria prodotti con immagine e ALTEZZA FISSA
------------------------------------------------------------------ */
.category-card {
  position: relative;
  display: block;
  background: $surface-base;
  color: $text-color;
  box-shadow: 0 2px 4px rgba($clr-brand-dark, 0.05);
  border: 1px solid $clr-border;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;
  height: 180px; /* ALTEZZA FISSA */

  a {
    text-decoration: none;
    color: inherit;
    display: block;
    position: relative;
    height: 100%;
  }

  @media (hover: hover) {
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba($clr-brand-dark, 0.12);
      border-color: rgba($clr-brand-dark, 0.2);
    }
  }

  /* ==================================================
     Vertical-bar con due pseudo-elementi
     ================================================== */
  .vertical-bar {
    position: absolute;
    left: 0;
    top: 0;
    width: 3px;
    height: 100%;
    pointer-events: none;
    z-index: 2;

    &::before,
    &::after {
      content: "";
      position: absolute;
      left: 0;
      width: 3px;
      background: $clr-border;
      height: 0;
      transition: height 0.5s ease, background 0.5s ease;
    }

    &::before { bottom: 50%; }
    &::after  { top: 50%; }
  }

  @media (hover: hover) {
    &:hover .vertical-bar::before,
    &:hover .vertical-bar::after {
      height: 50%;
      background: $clr-brand-dark;
    }

    &:hover .card-image img {
      transform: scale(1.05);
    }
  }

  /* Layout interno con flexbox per immagine + testo */
  .card-inner {
    display: flex;
    align-items: center;
    height: 100%;
  }

  /* ------------------------------------------------------------------
     Immagine con DIMENSIONE FISSA PIÙ LARGA
     e visualizzazione completa senza crop
  ------------------------------------------------------------------ */
  .card-image {
    flex: 0 0 320px;  /* Larghezza fissa AUMENTATA */
    height: 100%;     /* Altezza 100% del container */
    position: relative;
    overflow: hidden;
    background: $surface-base;

    .image-container {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;  /* Rimosso ogni padding */

      img {
        max-width: 100%;
        max-height: 100%;
        width: auto;
        height: auto;
        object-fit: contain;  /* CONTAIN invece di COVER - mostra tutta l'immagine */
        transition: transform 0.3s ease;
      }
    }
  }

  /* ------------------------------------------------------------------
     Placeholder invisibile per mantenere allineamento quando non c'è immagine
  ------------------------------------------------------------------ */
  /* Placeholder rimosso come richiesto - il contenuto occupa tutto lo spazio se manca l'immagine */

  /* Contenuto testo a DESTRA - sempre allineato */
  .card-content {
    flex: 1;
    padding: $space-2 $space-2-5;
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 0; /* Importante per text-overflow */
  }

  .card-title {
    font-size: clamp(1.5rem, 2.8vw, 2rem);
    margin: 0 0 $space-0-75;
    font-weight: 500;
    line-height: 1.2;
    font-family: 'Karla', sans-serif;
    color: $text-color; /* Colore standard invece che grigio */
    /* Gestione overflow testo */
    overflow: hidden;
    text-overflow: ellipsis;
    /* Max 2 righe */
    @include line-clamp(2, 1.2, $overflow: false);
  }

  .card-text {
    font-size: clamp(1rem, 1.8vw, 1.3rem);
    color: $text-color;
    margin: 0;
    line-height: 1.5;
    opacity: 0.85;
    overflow: hidden;
    text-overflow: ellipsis;
    @include line-clamp(2, 1.5, $overflow: false);
  }
}

/* ------------------------------------------------------------------
   Media queries responsive - Display grande (≤1700px)
------------------------------------------------------------------ */
@media (max-width: 1700px) {
  .category-card {
    height: 170px; /* Altezza fissa */

    .card-image {
      flex: 0 0 300px;  /* Larghezza ridotta */
    }

    .card-content {
      padding: $space-1-75 $space-2;
    }

    .card-title {
      font-size: clamp(1.4rem, 2.5vw, 1.8rem);
    }
  }
}

/* ------------------------------------------------------------------
   Media queries responsive - Display intorno a 1400px
------------------------------------------------------------------ */
@media (max-width: 1400px) {
  .category-card {
    height: 160px; /* Altezza fissa */

    .card-image {
      flex: 0 0 280px;  /* Larghezza ridotta */
    }

    .card-content {
      padding: $space-1-25 $space-1-5;
    }

    .card-title {
      font-size: clamp(1.2rem, 2.2vw, 1.6rem); /* Font più piccolo */
      margin-bottom: $space-0-5;
      /* Permettiamo 3 righe su schermi medi */
      @include line-clamp-lines(3, 1.2);
    }

    /* Sottotitolo con font ridotto */
    .card-text {
      font-size: clamp(0.85rem, 1.5vw, 1.1rem);
      @include line-clamp-lines(2, 1.5);
    }
  }
}

/* ------------------------------------------------------------------
   Media queries responsive - Display medio-grande (≤1300px)
------------------------------------------------------------------ */
@media (max-width: 1300px) {
  .category-card {
    height: 150px; /* Altezza fissa */

    .card-image {
      flex: 0 0 260px;  /* Larghezza ridotta */
    }

    .card-content {
      padding: $space-1 $space-1-25;
    }

    .card-title {
      font-size: clamp(1.1rem, 2vw, 1.4rem); /* Font ancora più piccolo */
      margin-bottom: $space-0-25;
      @include line-clamp-lines(3, 1.2);
    }

    .card-text {
      font-size: clamp(0.8rem, 1.4vw, 1rem);
      @include line-clamp-lines(2, 1.5);
    }
  }
}

/* ------------------------------------------------------------------
   Media queries responsive - Display intorno a 1100px
------------------------------------------------------------------ */
@media (max-width: 1100px) {
  .category-card {
    height: 140px; /* Altezza fissa ridotta */

    .card-image {
      flex: 0 0 240px;  /* Larghezza ridotta */
    }

    .card-content {
      padding: $space-0-75 $space-1;
    }

    .card-title {
      font-size: clamp(1rem, 1.8vw, 1.3rem); /* Font molto piccolo */
      margin-bottom: $space-0-25;
      font-weight: 600; /* Più bold per compensare dimensione */
      @include line-clamp-lines(3, 1.2);
    }

    /* Nascondiamo il sottotitolo per dare più spazio al titolo */
    .card-text {
      display: none;
    }
  }
}

/* ------------------------------------------------------------------
   Media queries responsive - Tablet (≤920px)
------------------------------------------------------------------ */
@media (max-width: 920px) {
  .category-card {
    height: 140px; /* Altezza fissa */

    .card-image {
      flex: 0 0 220px;  /* Larghezza ridotta per tablet */
    }

    .card-content {
      padding: $space-1 $space-1-25;
    }

    .card-title {
      font-size: clamp(1.1rem, 2.5vw, 1.4rem);
      font-weight: 600;
      @include line-clamp-lines(3, 1.2);
    }

    .card-text {
      display: none;
    }
  }
}

/* ------------------------------------------------------------------
   Media queries responsive - Mobile (≤480px)
------------------------------------------------------------------ */
@media (max-width: 480px) {
  .category-card {
    height: auto; /* Su mobile l'altezza è automatica */

    .card-inner {
      flex-direction: column;
      height: auto;
    }

    /* Immagine sopra il testo su mobile */
    .card-image {
      flex: none;
      width: 100%;
      height: 200px;
      order: 1;
    }

    .card-content {
      padding: $space-1-25 $space-1-5;
      width: 100%;
      order: 2;
    }

    .card-title {
      font-size: clamp(1.2rem, 4vw, 1.4rem);
      margin-bottom: $space-0-25;
      font-weight: 500;
      @include line-clamp-lines(3, 1.2);
    }

    /* Su mobile mostro di nuovo il sottotitolo */
    .card-text {
      // Qui il sorgente dichiarava `display: block` e due righe sotto
      // `-webkit-line-clamp: 2`. Il clamp funziona solo dentro un
      // `display: -webkit-box`, quindi il troncamento non e' MAI
      // avvenuto: su mobile il sottotitolo si allungava per tutte le
      // righe che aveva, su ogni browser. Corretto il 2026-09-09.
      // `$overflow: false` perche' `overflow: hidden` arriva gia'
      // dalla regola base di .card-text.
      @include line-clamp(2, 1.4, $overflow: false);
      font-size: clamp(0.85rem, 3vw, 1rem);
      line-height: 1.4;
      opacity: 0.85;
    }

    @media (hover: hover) {
      &:hover {
        transform: translateY(-1px);
      }
    }
  }
}

/* ------------------------------------------------------------------
   Media queries responsive - Mobile molto piccolo (≤360px)
------------------------------------------------------------------ */
@media (max-width: 360px) {
  .category-card {
    .card-image {
      height: 160px;
    }

    .card-content {
      padding: $space-1 $space-1-25;
    }

    .card-title {
      font-size: clamp(1.1rem, 4.5vw, 1.3rem);
    }

    .card-text {
      font-size: clamp(0.8rem, 3.5vw, 0.95rem);
    }
  }
}
</style>